import { h, render, Fragment, createContext } from 'preact'
import {
  useState, useEffect, useRef, useMemo, useCallback, useContext, useLayoutEffect, useReducer
} from 'preact/hooks'
import htmModule from 'htm'

/**
 * Templates use `htm` rather than JSX so the whole UI compiles with a plain
 * esbuild/Vite pass and stays readable in a debugger — no transform step to
 * reason about when something renders wrong on a device.
 */
export const html = htmModule.bind(h)

export {
  h, render, Fragment, createContext,
  useState, useEffect, useRef, useMemo, useCallback, useContext, useLayoutEffect, useReducer
}

/** Join class names, dropping falsy values. */
export function cx (...parts) {
  const out = []
  for (const p of parts) {
    if (!p) continue
    if (typeof p === 'string') out.push(p)
    else if (typeof p === 'object') {
      for (const [k, v] of Object.entries(p)) if (v) out.push(k)
    }
  }
  return out.join(' ')
}

/** Re-render when an Emitter fires any of `events`. */
export function useEmitter (target, events, deps = []) {
  const [, bump] = useReducer((n) => n + 1, 0)
  useEffect(() => {
    if (!target) return
    const list = Array.isArray(events) ? events : [events]
    const offs = list.map((e) => target.on(e, () => bump()))
    return () => offs.forEach((off) => off && off())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, ...deps])
}

/** Await an async value, re-running when `deps` change. */
export function useAsync (fn, deps = [], initial = null) {
  const [state, setState] = useState({ value: initial, loading: true, error: null })
  useEffect(() => {
    let alive = true
    setState((s) => ({ ...s, loading: true }))
    Promise.resolve()
      .then(fn)
      .then((value) => { if (alive) setState({ value, loading: false, error: null }) })
      .catch((error) => { if (alive) setState({ value: initial, loading: false, error }) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  return state
}

export function useOutsideClick (ref, onOutside, active = true) {
  useEffect(() => {
    if (!active) return
    const handler = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onOutside(event)
    }
    const key = (event) => { if (event.key === 'Escape') onOutside(event) }
    document.addEventListener('pointerdown', handler, true)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', handler, true)
      document.removeEventListener('keydown', key)
    }
  }, [ref, onOutside, active])
}

/** Media query as a boolean that updates on resize. */
export function useMedia (query) {
  const [matches, setMatches] = useState(
    () => typeof matchMedia === 'function' && matchMedia(query).matches
  )
  useEffect(() => {
    if (typeof matchMedia !== 'function') return
    const mq = matchMedia(query)
    const on = () => setMatches(mq.matches)
    mq.addEventListener('change', on)
    on()
    return () => mq.removeEventListener('change', on)
  }, [query])
  return matches
}
